import { useEffect, useMemo, useState } from "react";

import { IPart, TitleCase, IPartCategoryDefault, IPartCategory } from "../../models/Part";
import { IEbayAspect, IPartAspect } from "../../models/EbayOffer";
import useEbayAspects from "../../data/useEbayAspects";
import usePartDetails from "../../data/usePartDetails";
import classNames from "../../utils/classNames";
import useNotification from "../notifications/useNotifications";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import useSaveEbayOfferTitle from "../../data/useSaveEbayOfferTitle";
import { TextInputWithValue } from "../shared/Inputs/TextInputWithValue";
import toTitleCase from "../../utils/toTitleCase";
import usePartDefaultCategories from "../../data/usePartDefaultCategories";
import LoadingWheel from "../shared/LoadingWheel";
import LoadingPulse from "../shared/LoadingPulse";
import BulkUpdateSingleCategoryDefault from "../categoryEditor/BulkUpdate/BulkUpdateSingleCategoryDefault";
import AiTitleGenerator from "./AiTitleGenerator";

interface EbayListingTitleProps {
  part?: IPart;
  title?: string;
  onTable?: boolean;
  register?: any;
  getValues?: any;
  setValue?: any;
  watch?: any;
  isTemplate?: boolean;
  registerName?: string;
  onlyTitle?: boolean;
  editDefault?: boolean;
  /** When false, AI / blur / Save only update local/form state and do not persist the live offer title. */
  persistTitle?: boolean;
  /** When set, Save / blur / AI call this instead of writing the live offer title. */
  onSaveTitle?: (title: string) => void;
  /**
   * Template mode only. Supplies the aspect names offered as {{asp:...}} chips, since a
   * category template has no part to read aspects from.
   */
  templateCategory?: IPartCategory | null;
}

type IDefaults = {
  label: string;
  value: any;
  mergeTag: string;
  dataClass: string;
  tailwindClass: string;
  preserveCase: boolean;
};

const EBAY_TITLE_MAX = 80;

// Helper functions for title computation
const formatEngineCapacity = (engine: number | null | undefined) => {
  if (!engine) return engine;
  const rounded = Math.round(engine / 100) * 100;
  return (rounded / 1000).toFixed(1) + "L";
};

const formatSuffix = (
  value: string | number | null | undefined,
  suffix: string
) => {
  if (!value) return value;
  return value + suffix;
};

const formatPrefix = (
  value: string | number | null | undefined,
  prefix: string
) => {
  if (!value) return value;
  return prefix + value;
};

const escapeRegex = (str: string) =>
  str.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");

// The highlighted preview is rendered with dangerouslySetInnerHTML, and aspect values are
// free-form user input, so everything interpolated into it has to be escaped first.
const escapeHtml = (str: string) =>
  str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** Aspect (item specific) merge tag, e.g. {{asp:Brand}}. */
export const buildAspectMergeTag = (name: string) =>
  `{{asp:${name.trim().replace(/\s+/g, " ")}}}`;

// Source string rather than a shared /g/ regex object - a module-level global regex carries
// lastIndex between calls and would skip matches on every other invocation.
// Group 1 is the aspect name. Whitespace inside the braces is tolerated to match the
// server-side parser, so {{ asp: Brand }} behaves the same as {{asp:Brand}}.
const ASPECT_TAG_PATTERN = "\\{\\{\\s*asp:\\s*([^{}]+?)\\s*\\}\\}";

const normaliseAspectName = (name: string) =>
  name.trim().replace(/\s+/g, " ").toLowerCase();

const formatWordByTitleCase = (word: string, titleCase: TitleCase, preserveCase: boolean): string => {
  if (preserveCase) return word;
  switch (titleCase) {
    case TitleCase.Upper:
      return word.toUpperCase();
    case TitleCase.Sentence:
      return toTitleCase(word);
    case TitleCase["No Change"]:
    default:
      return word;
  }
};

/**
 * Resolves {{asp:Name}} tags in one pass, keyed on aspect name rather than on an exact tag
 * string, so whitespace and casing variants behave the same as they do server-side.
 *
 * @param stripUnresolved When true, a tag naming an aspect that isn't present (or has no value)
 * is removed. Pass false while a part's aspects are still loading, otherwise the tag would be
 * stripped on the first pass and there'd be nothing left to resolve once they arrive.
 */
const replaceAspectMergeTags = (
  title: string,
  aspectDefaults: IDefaults[],
  titleCase: TitleCase,
  stripUnresolved: boolean
): string => {
  if (!title.includes("{{")) return title;

  const lookup = new Map<string, string>();

  aspectDefaults.forEach((def) => {
    if (!def.value) return;

    const key = normaliseAspectName(def.label);
    // First entry wins, so the result doesn't depend on aspect ordering.
    if (lookup.has(key)) return;

    lookup.set(
      key,
      formatWordByTitleCase(def.value.toString(), titleCase, def.preserveCase)
    );
  });

  // Group 2 is the single trailing space, kept when the tag resolves and dropped with the tag
  // when it doesn't, so removing a tag never leaves a double space behind.
  return title.replace(
    new RegExp(`${ASPECT_TAG_PATTERN}(\\s?)`, "gi"),
    (match, name: string, trail: string) => {
      const value = lookup.get(normaliseAspectName(name));

      if (value) return value + trail;

      return stripUnresolved ? "" : match;
    }
  );
};

/**
 * @param aspectDefaults Aspect entries, resolved separately from the fixed tags since they're
 * matched by name rather than by literal tag text.
 * @param stripUnresolvedAspects Forwarded to replaceAspectMergeTags.
 */
const replaceMergeTags = (
  title: string,
  defaults: IDefaults[],
  aspectDefaults: IDefaults[],
  titleCase: TitleCase,
  stripUnresolvedAspects: boolean = true
): string => {
  let formatted = title;

  defaults.forEach((def) => {
    if (!def.mergeTag) return;

    const tagRegex = new RegExp(escapeRegex(def.mergeTag), "gi");

    if (def.value) {
      const valueStr = def.value.toString();
      const casedValue = formatWordByTitleCase(
        valueStr,
        titleCase,
        def.preserveCase
      );
      // Returned from a function so the value is literal - passing it as a replacement string
      // would let '$&' / '$1' in real data (part numbers, engine codes) corrupt the title.
      formatted = formatted.replace(tagRegex, () => casedValue);
    } else {
      formatted = formatted.replace(new RegExp(`${escapeRegex(def.mergeTag)}\\s?`, "gi"), "");
    }
  });

  // Aspects run last so both tag families are in place before the caller collapses whitespace.
  formatted = replaceAspectMergeTags(
    formatted,
    aspectDefaults,
    titleCase,
    stripUnresolvedAspects
  );

  return formatted;
};

/**
 * Builds the defaults array for title computation
 * @param part - The part to get values from
 * @param categoryDefaults - Optional category defaults
 * @param isTemplate - Whether in template mode (nullifies values if true)
 * @returns Array of default values with merge tags
 */
function buildDefaultsArray(
  part: IPart | undefined,
  categoryDefaults?: IPartCategoryDefault | null,
  isTemplate: boolean = false
): IDefaults[] {
  return [
    { label: "Category Name", value: isTemplate ? null : part?.category?.name ?? part?.title, mergeTag: "{{categoryName}}", dataClass: "data-yellow", tailwindClass: "bg-yellow-300", preserveCase: false },
    { label: "Category Alias", value: isTemplate ? null : categoryDefaults?.categoryAlias, mergeTag: "{{categoryAlias}}", dataClass: "data-yellow", tailwindClass: "bg-yellow-400", preserveCase: false },
    { label: "Year", value: isTemplate ? null : part?.vehicle?.dvlaYearOfManufacture, mergeTag: "{{year}}", dataClass: "data-green", tailwindClass: "bg-green-300", preserveCase: false },
    { label: "Make", value: isTemplate ? null : part?.vehicle?.combinedMake, mergeTag: "{{make}}", dataClass: "data-blue", tailwindClass: "bg-blue-300", preserveCase: false },
    { label: "Model", value: isTemplate ? null : part?.vehicle?.combinedModel, mergeTag: "{{model}}", dataClass: "data-orange", tailwindClass: "bg-orange-300", preserveCase: false },
    { label: "Short Model", value: isTemplate ? null : part?.vehicle?.modelRangeDescription, mergeTag: "{{shortModel}}", dataClass: "data-orange", tailwindClass: "bg-orange-400", preserveCase: false },
    { label: "MK", value: isTemplate ? null : formatPrefix(part?.vehicle?.mark, "MK"), mergeTag: "{{mk}}", dataClass: "data-lime", tailwindClass: "bg-lime-300", preserveCase: true },
    { label: "Series", value: isTemplate ? null : part?.vehicle?.modelSeries, mergeTag: "{{series}}", dataClass: "data-lime", tailwindClass: "bg-lime-300", preserveCase: true },
    { label: "Fuel Type", value: isTemplate ? null : part?.vehicle?.fuelType, mergeTag: "{{fuelType}}", dataClass: "data-teal", tailwindClass: "bg-teal-300", preserveCase: false },
    { label: "Door", value: isTemplate ? null : formatSuffix(part?.vehicle?.numberOfDoors, " Doors"), mergeTag: "{{door}}", dataClass: "data-cyan", tailwindClass: "bg-cyan-300", preserveCase: false },
    { label: "Body Style", value: isTemplate ? null : part?.vehicle?.bodyStyleDescription, mergeTag: "{{bodyStyle}}", dataClass: "data-indigo", tailwindClass: "bg-indigo-300", preserveCase: false },
    { label: "Engine Capacity", value: isTemplate ? null : formatEngineCapacity(part?.vehicle?.engineCapacity), mergeTag: "{{engineCapacity}}", dataClass: "data-slate", tailwindClass: "bg-slate-300", preserveCase: true },
    { label: "Engine Code", value: isTemplate ? null : part?.vehicle?.engineModelCode, mergeTag: "{{engineCode}}", dataClass: "data-purple", tailwindClass: "bg-purple-300", preserveCase: true },
    { label: "BHP", value: isTemplate ? null : formatSuffix(part?.vehicle?.maximumPowerBHP, " BHP"), mergeTag: "{{bhp}}", dataClass: "data-fuchsia", tailwindClass: "bg-fuchsia-300", preserveCase: true },
    { label: "No. Gears", value: isTemplate ? null : formatSuffix(part?.vehicle?.combinedForwardGears, " Speed"), mergeTag: "{{noGears}}", dataClass: "data-pink", tailwindClass: "bg-pink-300", preserveCase: false },
    { label: "Transmission", value: isTemplate ? null : part?.vehicle?.combinedTransmission, mergeTag: "{{transmission}}", dataClass: "data-rose", tailwindClass: "bg-rose-300", preserveCase: false },
    { label: "Mileage", value: isTemplate ? null : (part?.vehicle?.odometerInMiles === 0 ? null : part?.vehicle?.odometerInMiles), mergeTag: "{{mileage}}", dataClass: "data-amber", tailwindClass: "bg-amber-300", preserveCase: false },
    { label: "Colour", value: isTemplate ? null : part?.vehicle?.colourCurrent, mergeTag: "{{colour}}", dataClass: "data-amber", tailwindClass: "bg-amber-300", preserveCase: false },
    { label: "Colour Code", value: isTemplate ? null : part?.vehicle?.vinvisColourCode, mergeTag: "{{colourCode}}", dataClass: "data-amber", tailwindClass: "bg-amber-300", preserveCase: false },
    { label: "Tag Number", value: isTemplate ? null : part?.tagNumber, mergeTag: "{{tagNumber}}", dataClass: "data-green", tailwindClass: "bg-green-300", preserveCase: true },
    { label: "Part Number", value: isTemplate ? null : (part?.oePartNumber ?? part?.partNumber), mergeTag: "{{partNumber}}", dataClass: "data-blue", tailwindClass: "bg-blue-300", preserveCase: true },
  ];
}

/**
 * Builds merge-tag entries for a part's aspects (item specifics).
 *
 * Kept separate from buildDefaultsArray because these are dynamic - one entry per aspect the
 * part actually has - and are rendered as their own chip group rather than in the fixed grid.
 *
 * @param aspects The part's aspects, or the available aspect names in template mode
 * @param isTemplate Template mode shows every name with no value, so tags stay intact
 */
function buildAspectDefaults(
  aspects: IPartAspect[] | undefined | null,
  isTemplate: boolean = false
): IDefaults[] {
  if (!Array.isArray(aspects)) return [];

  const seen = new Set<string>();

  return aspects.reduce<IDefaults[]>((acc, aspect) => {
    const name = aspect?.name?.trim();
    if (!name) return acc;

    const key = normaliseAspectName(name);
    if (seen.has(key)) return acc;
    seen.add(key);

    const value = aspect.value?.trim();

    // Aspects with no value are only worth offering in template mode, where the tag itself is
    // the thing being inserted.
    if (!isTemplate && !value) return acc;

    acc.push({
      label: name,
      value: isTemplate ? null : value,
      mergeTag: buildAspectMergeTag(name),
      dataClass: "data-sky",
      tailwindClass: "bg-sky-300",
      // Aspect values are things like "OEM", "LH" and part numbers - title casing mangles them.
      preserveCase: true,
    });

    return acc;
  }, []);
}

/**
 * Computes the eBay listing title for a part based on merge tags and category defaults
 *
 * NOTE: this logic is mirrored server-side by PartCategoryDefaults.ParseTitleFromStructure, which
 * is what actually builds the title sent to eBay when category defaults are applied to an offer.
 * Adding or changing a merge tag here means changing it there too, otherwise the preview an
 * operator sees stops matching the listing that goes out. See AspectMergeTagTests for the
 * behaviour the two are expected to share.
 * @param part - The part to compute the title for
 * @param categoryDefaults - Optional category defaults (if not provided, will use part.category?.partCategoryDefaults[0])
 * @returns The computed title string
 */
export function computeEbayListingTitle(
  part: IPart,
  categoryDefaults?: IPartCategoryDefault | null,
  skipSaved: boolean = false
): string {
  if (!part) return "";

  // Use provided categoryDefaults or fall back to part's category defaults
  const defaults = categoryDefaults ?? part.category?.partCategoryDefaults?.[0] ?? null;

  // Prefer saved title if available
  const savedTitle = skipSaved ? null : part.offers?.[0]?.title;

  // Determine the base title to use
  let targetTitle = savedTitle || `{{year}} {{make}} {{shortModel}} {{categoryName}}`;

  // Use category titleStructure if no saved title exists
  if (!savedTitle && defaults?.titleStructure) {
    targetTitle = defaults.titleStructure;
  }

  // Build defaults array using shared function
  const defaultsArray = buildDefaultsArray(part, defaults, false);

  // Callers on table rows may hold a part whose aspects were never loaded; in that case the
  // aspect tags resolve to nothing and are stripped, same as any other absent value.
  const aspectsArray = buildAspectDefaults(part.aspects, false);

  // Get title case from category defaults
  const titleCase = (defaults?.titleCase as TitleCase) ?? TitleCase["No Change"];

  // Apply title case formatting to the entire title first
  let processed = formatWordByTitleCase(targetTitle, titleCase, false);

  // Replace merge tags with actual values
  const merged = replaceMergeTags(processed, defaultsArray, aspectsArray, titleCase);

  // Clean up extra spaces
  return merged.trim().replace(/\s{2,}/g, " ");
}

export default function EbayListingTitle({
  part,
  title,
  onTable = false,
  register,
  getValues,
  setValue,
  isTemplate = false,
  registerName = "title",
  onlyTitle = false,
  editDefault = false,
  persistTitle = true,
  onSaveTitle,
  templateCategory,
}: EbayListingTitleProps) {
  const hasExistingDefaults = (part?.category?.partCategoryDefaults?.length ?? 0) > 0;
  // Explicit title override (e.g. staged title on the optimiser) wins over the live offer.
  const hasTitleOverride = title != null;

  // Fetch unless we're "title only" AND already have defaults locally
  const shouldFetchDefaults = !onlyTitle || !hasExistingDefaults;

  const { defaultPartCategory } = usePartDefaultCategories({
    part: part ?? { categoryId: "" } as IPart,
    enabled: shouldFetchDefaults,
  }); // Disable if no category defaults to fetch
  const { saveTitle } = useSaveEbayOfferTitle();
  const { addNotification } = useNotification();

  const [openEditDefaults, setOpenEditDefaults] = useState(false);
  // The aspect menu opens on hover; this pins it open on click so it also works on touch.
  const [aspectMenuPinned, setAspectMenuPinned] = useState(false);

  // Prefer an existing saved offer title when available (non-template mode)
  const savedTitle = !isTemplate ? part?.offers?.[0]?.title : undefined;

  const initialTitle = hasTitleOverride
    ? title
    : onTable
      ? title
      : getValues?.(registerName) ||
        savedTitle ||
        (isTemplate ? "{{year}} {{make}} {{shortModel}} {{categoryName}}" : "");

  const [currentTitle, setCurrentTitle] = useState<string>(initialTitle);

  const [titleLabel, setTitleLabel] = useState(
    `Listing Title ${currentTitle?.length > 0 ? `(${currentTitle?.length}/80)` : ""
    }`
  );

  const defaults = useMemo(() => {
    const categoryDefaultsToUse =
      onlyTitle && !hasExistingDefaults
        ? defaultPartCategory.data?.data
        : part?.category?.partCategoryDefaults?.[0];

    return buildDefaultsArray(part, categoryDefaultsToUse, isTemplate);
  }, [part, onlyTitle, hasExistingDefaults, defaultPartCategory.data, isTemplate]);

  // ---- Aspect (item specific) merge tags ----------------------------------------------------
  // Non-template: the part's own aspects. Parts arriving from a table often have no aspects
  // loaded, so hydrate via the same details endpoint the other cells use.
  //
  // Deliberately limited to the full editor: onlyTitle renders one instance per table row, and
  // hydrating there would add a details fetch per row. Those rows show the saved offer title,
  // which the server has already resolved, so there are no aspect tags left to substitute.
  const needsAspectHydration =
    !isTemplate && !onlyTitle && !!part?.id && !Array.isArray(part?.aspects);
  const { partDetails, isLoading: aspectDetailsLoading } = usePartDetails(
    part?.id,
    needsAspectHydration
  );

  // Template: no part exists, so offer the names the category is configured with - the eBay
  // category's item specifics plus any custom aspects, matching CategoryAspectsDefaults.
  const templateDefaults = templateCategory?.partCategoryDefaults?.[0];
  const templateEbayCategoryId = isTemplate ? templateDefaults?.ebayCategoryId : null;
  const { aspects: templateEbayAspects } = useEbayAspects(templateEbayCategoryId ?? "");

  const aspectDefaults = useMemo(() => {
    if (isTemplate) {
      // useEbayAspects already unwraps the response envelope, so .data is the array itself.
      const fromEbay: IPartAspect[] = (templateEbayAspects.data ?? []).map(
        (a: IEbayAspect) => ({ name: a.localizedAspectName, value: "" } as IPartAspect)
      );
      const custom = templateDefaults?.categoryCustomAspects ?? [];

      return buildAspectDefaults([...custom, ...fromEbay], true);
    }

    const partAspects = Array.isArray(part?.aspects) ? part?.aspects : partDetails?.aspects;

    return buildAspectDefaults(partAspects, false);
  }, [isTemplate, templateEbayAspects.data, templateDefaults, part?.aspects, partDetails]);

  // While a part's aspects are still in flight, leave {{asp:...}} tags alone rather than
  // stripping them - otherwise there's nothing left to substitute when they land.
  const aspectsPending = needsAspectHydration && aspectDetailsLoading;

  const allDefaults = useMemo(
    () => [...defaults, ...aspectDefaults],
    [defaults, aspectDefaults]
  );

  // The individual aspect rows are hidden until the menu opens, so the cell itself carries the
  // "in use" highlight the fixed chips get - otherwise there'd be no sign an aspect is in play.
  const anyAspectInTitle = useMemo(
    () =>
      aspectDefaults.some((def) => {
        const valueToUse = isTemplate ? def.mergeTag : def.value;

        return (
          !!valueToUse &&
          !!currentTitle?.toLowerCase().includes(valueToUse.toString().toLowerCase())
        );
      }),
    [aspectDefaults, isTemplate, currentTitle]
  );

  const highlightDefaultsInTitle = (title: string, defaults: IDefaults[], isTemplate: boolean): string => {
    if (!title) return "";

    // Escape up front: the result is fed to dangerouslySetInnerHTML and aspect values are
    // free-form user input. Values are escaped the same way below so matching still lines up.
    let highlighted = escapeHtml(title);

    const allValues = defaults
      .filter((d) => (isTemplate ? d.mergeTag : d.value))
      .map((d) => escapeHtml((isTemplate ? d.mergeTag : d.value)!.toString()));

    const containedValues = new Set<string>();
    allValues.forEach((v, i) =>
      allValues.forEach((o, j) => {
        if (i !== j && o.toLowerCase().includes(v.toLowerCase())) {
          containedValues.add(v);
        }
      })
    );

    defaults.forEach((def) => {
      const valueToUse = isTemplate ? def.mergeTag : def.value;
      if (!valueToUse) return;

      const valueStr = escapeHtml(valueToUse.toString());

      if (containedValues.has(valueStr)) {
        const hasLonger = allValues.some(
          (o) =>
            o !== valueStr &&
            o.toLowerCase().includes(valueStr.toLowerCase()) &&
            highlighted.toLowerCase().includes(o.toLowerCase())
        );
        if (hasLonger) return;
      }

      const regex = new RegExp(escapeRegex(valueStr), "gi");
      if (regex.test(highlighted)) {
        highlighted = highlighted
          .replace(regex, `<span ${def.dataClass}>${valueStr}</span>`);
      }
    });

    return highlighted;
  };


  const [rawTitle, setRawTitle] = useState<string>(
    isTemplate ? highlightDefaultsInTitle(initialTitle, allDefaults, true) : initialTitle
  );



  const handleSetValue = (name: string, value: string) => {
    const categoryDefaults = !hasExistingDefaults
      ? defaultPartCategory.data?.data
      : part?.category?.partCategoryDefaults?.[0];

    const titleCase =
      (categoryDefaults?.titleCase as TitleCase) ?? TitleCase["No Change"];

    let processed = value;

    if (isTemplate) {
      // Template mode: keep merge tags intact, don't replace
      const highlighted = highlightDefaultsInTitle(processed, allDefaults, true);
      setCurrentTitle(processed);
      setRawTitle(highlighted);
      setTitleLabel(
        `Listing Title ${processed?.length ? `(${processed.length}/80)` : ""}`
      );
      if (!onTable) setValue?.(name, processed);
      return;
    }

    let preCased = formatWordByTitleCase(processed, titleCase, false);

    const merged = replaceMergeTags(
      preCased,
      defaults,
      aspectDefaults,
      titleCase,
      !aspectsPending
    );

    const highlighted = highlightDefaultsInTitle(merged, allDefaults, false);

    setCurrentTitle(merged);
    setRawTitle(highlighted);
    setTitleLabel(
      `Listing Title ${merged?.length ? `(${merged.length}/80)` : ""}`
    );
    if (!onTable) setValue?.(name, merged);
  };


  const changeTitle = (def: IDefaults) => {
    let newTitle = currentTitle || "";
    const valueToUse = isTemplate ? def.mergeTag : def.value;

    if (!valueToUse) {
      addNotification({
        variant: "info",
        primaryText: "No value found",
      });
      return;
    }

    const word = valueToUse.toString();
    const escapedWord = word.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
    const regex = new RegExp(escapedWord, "i");

    if (regex.test(newTitle)) {
      newTitle = newTitle
        .replace(regex, "")
        .trim()
        .replace(/\s{2,}/g, " ");
    } else {
      newTitle = `${newTitle} ${word}`.trim().replace(/\s{2,}/g, " ");
    }

    handleSetValue(registerName, newTitle);
  };

  // AI titles arrive fully formed - skip merge-tag/title-case processing so casing like "OEM" and "LH" survives.
  const applyAiTitle = (nextTitle: string) => {
    setCurrentTitle(nextTitle);
    setRawTitle(highlightDefaultsInTitle(nextTitle, allDefaults, false));
    setTitleLabel(`Listing Title ${nextTitle?.length ? `(${nextTitle.length}/80)` : ""}`);
    if (!onTable) setValue?.(registerName, nextTitle);
    // Persist immediately so "Use AI title" both fills the box and saves.
    // Pass the title through explicitly since setCurrentTitle hasn't flushed yet.
    // Staging/form-only consumers pass onSaveTitle or set persistTitle=false.
    handleSaveTitle(nextTitle);
  };

  const handleSaveTitle = (titleOverride?: string) => {
    if (isTemplate || !part) return;
    const nextTitle = titleOverride ?? currentTitle;
    if (onSaveTitle) {
      onSaveTitle(nextTitle);
      return;
    }
    if (!persistTitle) return;
    saveTitle(part, nextTitle);
  };

  useEffect(() => {
    if (defaultPartCategory.isFetching) return;
    // Wait for aspects before merging, so {{asp:...}} tags aren't stripped from the title
    // before there's anything to substitute into them.
    if (aspectsPending) return;

    const catDefaults = !hasExistingDefaults
      ? defaultPartCategory.data?.data
      : part?.category?.partCategoryDefaults?.[0];

    // Template mode: keep merge tags intact
    if (isTemplate) {
      let targetTitle = currentTitle || `{{year}} {{make}} {{shortModel}} {{categoryName}}`;

      if (catDefaults?.titleStructure) targetTitle = catDefaults.titleStructure;

      setCurrentTitle(targetTitle);
      setRawTitle(highlightDefaultsInTitle(targetTitle, allDefaults, true));
      setTitleLabel(
        `Listing Title ${targetTitle?.length ? `(${targetTitle.length}/80)` : ""}`
      );
      if (!onTable) setValue?.(registerName, targetTitle);
      return;
    }

    // Non-template mode
    // Explicit title override (staged) wins. Otherwise onlyTitle prefers the live
    // saved title so table rows stay in sync; editable mode keeps currentTitle.
    let targetTitle = hasTitleOverride
      ? title!
      : onlyTitle
        ? (savedTitle || currentTitle || `{{year}} {{make}} {{shortModel}} {{categoryName}}`)
        : (currentTitle || savedTitle || `{{year}} {{make}} {{shortModel}} {{categoryName}}`);

    if (!hasTitleOverride && !savedTitle && catDefaults?.titleStructure) {
      targetTitle = catDefaults.titleStructure;
    }

    handleSetValue(registerName, targetTitle);
  }, [allDefaults, defaultPartCategory.data, defaultPartCategory.isLoading, isTemplate, savedTitle, title, hasTitleOverride, aspectsPending]);

  if (defaultPartCategory.isFetching && onlyTitle) {
    return <LoadingPulse />;
  }

  if (defaultPartCategory.isFetching) {
    return <LoadingWheel />;
  }

  if (onlyTitle && onTable) {
    return (
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <input
          type="text"
          className="h-8 min-w-0 flex-1 rounded-md border border-sky-200 bg-white px-2.5 py-1 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 disabled:cursor-not-allowed disabled:bg-slate-100"
          value={currentTitle}
          onChange={(e) => handleSetValue(registerName, e.target.value)}
          onBlur={() => handleSaveTitle()}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.blur();
            }
          }}
          maxLength={EBAY_TITLE_MAX}
          disabled={part?.soldDate != null}
          title={
            part?.soldDate != null
              ? "Cannot edit title for sold parts"
              : "Edit listing title - saves when you leave the field or press Enter"
          }
          autoComplete="off"
        />
        <span
          className={classNames(
            "shrink-0 tabular-nums text-[11px]",
            (currentTitle?.length ?? 0) > EBAY_TITLE_MAX
              ? "font-semibold text-amber-700"
              : "text-sky-700/80"
          )}
          title="Characters in the title (eBay max 80)"
        >
          {(currentTitle?.length ?? 0)}/{EBAY_TITLE_MAX}
        </span>
      </div>
    );
  }

  if (onlyTitle) {
    return (
      <div className="flex gap-x-1">
        <h2 className="font-semibold">{titleLabel}: </h2> <span>{currentTitle}</span>
      </div>
    );
  }

  return (
    <>
      <BulkUpdateSingleCategoryDefault
        open={openEditDefaults}
        setOpen={setOpenEditDefaults}
        selectedCategories={part?.category ? [part.category] : []}
        startingField={"titleStructure"}
        startingValue={part?.category?.partCategoryDefaults?.[0]?.titleStructure}
      />

      {/* Defaults grid */}
      <div className={classNames(onTable ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4" : "xl:grid-cols-5", "col-span-2 grid grid-cols-2 md:grid-cols-3 ")}>
        {defaults.map((def, i) => {
          const valueToUse = isTemplate ? def.mergeTag : def.value;
          const exists =
            valueToUse &&
            currentTitle
              ?.toLowerCase()
              .includes(valueToUse.toString().toLowerCase());

          return (
            <div
              key={i}
              className={classNames(
                exists ? def.tailwindClass : "",
                onTable ? "p-1 text-sm" : "p-2",
                " text-center border border-gray-300 cursor-pointer hover:bg-gray-200 select-none"
              )}
              onClick={() => changeTitle(def)}
            >
              <span>{def.label}</span>
            </div>
          );
        })}

        {/* Aspects (item specifics) sit in one grid cell rather than a chip each - the list is
            dynamic and an eBay category can expose 30+. Opens on hover, or click to pin it. The
            group is named so the table row's own "group" class (see VehiclePagedTable) can't open it.
            Selecting an option inserts {{asp:Name}} in template mode, the value otherwise. */}
        {aspectDefaults.length > 0 && (
          <div
            className={classNames(
              anyAspectInTitle ? "bg-sky-300" : "",
              onTable ? "p-1 text-sm" : "p-2",
              "group/aspects relative text-center border border-gray-300 cursor-pointer hover:bg-gray-200 select-none"
            )}
            onClick={() => setAspectMenuPinned((prev) => !prev)}
            // Pinning is only needed where hover doesn't exist, so a mouse leaving can drop it.
            onMouseLeave={() => setAspectMenuPinned(false)}
            title={
              isTemplate
                ? "Insert an aspect merge tag"
                : "Insert an aspect value"
            }
          >
            <span>Aspects ({aspectDefaults.length})</span>

            {/* top-full with no margin keeps the hover unbroken between cell and panel. */}
            <div
              className={classNames(
                aspectMenuPinned ? "block" : "hidden group-hover/aspects:block",
                "absolute left-0 top-full z-30 max-h-60 w-full min-w-[14rem] cursor-default overflow-auto bg-white text-left shadow-lg ring-1 ring-black ring-opacity-5"
              )}
            >
              <p className="border-b border-gray-200 px-2 py-1 text-xs text-gray-500">
                {isTemplate ? "Inserts a {{asp:name}} tag" : "Inserts the value"}
              </p>

              {aspectDefaults.map((def) => {
                const valueToUse = isTemplate ? def.mergeTag : def.value;
                const exists =
                  valueToUse &&
                  currentTitle
                    ?.toLowerCase()
                    .includes(valueToUse.toString().toLowerCase());

                return (
                  <div
                    key={def.mergeTag}
                    className={classNames(
                      exists ? def.tailwindClass : "",
                      "flex cursor-pointer items-baseline justify-between gap-x-2 px-2 py-1 text-sm hover:bg-gray-100"
                    )}
                    onClick={(e) => {
                      // The cell behind this toggles the menu; don't let that fire too.
                      e.stopPropagation();
                      changeTitle(def);
                    }}
                    title={isTemplate ? def.mergeTag : `${def.label}: ${def.value}`}
                  >
                    <span className="truncate">{def.label}</span>
                    {!isTemplate && (
                      <span className="shrink-0 truncate text-xs text-gray-500">
                        {def.value}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {register ? (
        <TextInputWithValue
          label={titleLabel}
          register={register ?? {}}
          registerName={registerName}
          cols="sm:col-span-2"
          disabled={part && part.soldDate !== null}
          value={currentTitle}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleSetValue(registerName, e.target.value)
          }
        />
      ) : (
        <div className="w-full flex gap-x-4 py-2">
          <div className={classNames(onTable ? "w-full" : "w-1/2", "flex flex-col gap-y-2")}>
            <label className="text-sm font-medium text-gray-500">
              Listing Title{" "} {currentTitle?.length > 0 ? `(${currentTitle?.length}/80)` : ""}
            </label>
            <input
              type="text"
              className={classNames(onTable ? "!px-2 !py-1 !text-sm" : "", "w-full flex-1 text-sm text-gray-900 border-1 border-gray-400")}
              value={currentTitle}
              onChange={(e) => handleSetValue(registerName, e.target.value)}
            />
          </div>
          {!isTemplate && (
            <div className="">
              <ButtonInput
                label="Save"
                isSubmit={false}
                onClick={() => handleSaveTitle()}
                classes="h-8 mt-7"
              />
            </div>
          )}
        </div>
      )}

      {!isTemplate && part && (
        <div className="col-span-2 py-1">
          <AiTitleGenerator part={part} onApply={applyAiTitle} disabled={part.soldDate != null} />
        </div>
      )}

      {/* Raw highlighted preview */}
      <div
        className="block pt-1 text-sm col-span-2 [&>[data-green]]:bg-green-300 [&>[data-lime]]:bg-lime-300 [&>[data-teal]]:bg-teal-300 [&>[data-cyan]]:bg-cyan-300 [&>[data-indigo]]:bg-indigo-300 [&>[data-slate]]:bg-slate-300 [&>[data-purple]]:bg-purple-300 [&>[data-fuchsia]]:bg-fuchsia-300 [&>[data-pink]]:bg-pink-300 [&>[data-rose]]:bg-rose-300 [&>[data-amber]]:bg-amber-300 [&>[data-blue]]:bg-blue-300 [&>[data-orange]]:bg-orange-300 [&>[data-yellow]]:bg-yellow-300 [&>[data-sky]]:bg-sky-300"
        dangerouslySetInnerHTML={{ __html: rawTitle }}
      />

      {(editDefault && part?.category) && (
        <ButtonInput
          label={hasExistingDefaults ? "Edit Category Default" : "Add Category Default"}
          isSubmit={false}
          onClick={() => setOpenEditDefaults(true)}
          classes="mt-4"
        />
      )}

    </>
  );
}
